import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const blogPosts = [
  {
    slug: 'missed-calls-revenue-killer-2025',
    title: 'Missed Calls: The Silent Revenue Killer in 2025',
    excerpt: 'Why 62% of business calls go unanswered and how this translates to $74,400+ in monthly lost revenue for service businesses.',
    category: '2025 Trends',
    date: 'January 18, 2025',
    readTime: '8 min read',
    image: 'https://public.readdy.ai/ai/img_res/7093ca5a2cd8c95ae7bdb01a9065bf17.jpg',
    featured: true,
  },
  {
    slug: 'after-hours-calls-2025-data',
    title: 'The 2025 After-Hours Call Report: What the Data Shows',
    excerpt: '73% of high-value leads call outside business hours. New research reveals when your best prospects are calling—and why you\'re missing them.',
    category: 'Research',
    date: 'January 15, 2025',
    readTime: '10 min read',
    image: 'https://readdy.ai/api/search-image?query=Night%20time%20city%20office%20building%20with%20lights%20on%20showing%20late%20night%20business%20operations%20and%2024%20hour%20service%20concept%20with%20modern%20architecture&width=600&height=400&seq=blog7&orientation=landscape',
    featured: true,
  },
  {
    slug: 'ai-vs-voicemail-2025',
    title: 'AI Phone Agents vs. Voicemail: 2025 Conversion Rates',
    excerpt: 'Why 85% of callers won\'t leave a voicemail—and how AI phone agents are converting 4x more leads than traditional answering systems.',
    category: 'Comparison',
    date: 'January 12, 2025',
    readTime: '7 min read',
    image: 'https://readdy.ai/api/search-image?query=Futuristic%20AI%20assistant%20concept%20with%20voice%20waves%20and%20neural%20network%20visualization%20representing%20intelligent%20phone%20conversation%20technology%20in%20teal%20and%20white%20colors&width=600&height=400&seq=blog2&orientation=landscape',
    featured: true,
  },
];

const BlogPage = () => {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-1 pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-navy via-navy/95 to-navy/90 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              PointCalls Blog
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              The latest research and insights on missed calls, AI phone technology, and maximizing lead capture in 2025.
            </p>
          </div>
        </section>

        {/* Featured Posts */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-navy mb-8">Featured: Missed Calls in 2025</h2>
            <div className="grid lg:grid-cols-3 gap-8">
              {blogPosts.map((post) => (
                <Link 
                  key={post.slug}
                  to={`/blog/${post.slug}`} 
                  className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 cursor-pointer"
                >
                  <div className="h-52 overflow-hidden">
                    <img 
                      src={post.image} 
                      alt={post.title} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs font-semibold text-teal uppercase tracking-wide">{post.category}</span>
                      <span className="text-xs text-gray-400">•</span>
                      <span className="text-xs text-gray-500">{post.readTime}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-navy mb-3 group-hover:text-teal transition-colors line-clamp-2">{post.title}</h3>
                    <p className="text-sm text-gray-600 line-clamp-2">{post.excerpt}</p>
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <span className="text-xs text-gray-500">{post.date}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Key Stats Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl font-bold text-navy mb-4">2025 Missed Call Statistics</h2>
              <p className="text-gray-600">The numbers every service business needs to know</p>
            </div>
            <div className="grid md:grid-cols-4 gap-6">
              <div className="bg-white rounded-xl p-6 text-center shadow-md">
                <div className="text-4xl font-bold text-teal mb-2">62%</div>
                <p className="text-sm text-gray-600">of business calls go unanswered</p>
              </div>
              <div className="bg-white rounded-xl p-6 text-center shadow-md">
                <div className="text-4xl font-bold text-teal mb-2">85%</div>
                <p className="text-sm text-gray-600">of callers won't leave voicemail</p>
              </div>
              <div className="bg-white rounded-xl p-6 text-center shadow-md">
                <div className="text-4xl font-bold text-teal mb-2">73%</div>
                <p className="text-sm text-gray-600">of leads call after hours</p>
              </div>
              <div className="bg-white rounded-xl p-6 text-center shadow-md">
                <div className="text-4xl font-bold text-teal mb-2">$1,200</div>
                <p className="text-sm text-gray-600">average value per missed lead</p>
              </div>
            </div>
          </div>
        </section>

        {/* Newsletter CTA */}
        <section className="py-16 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-gradient-to-br from-teal/10 to-teal/5 rounded-3xl p-10 border border-teal/20">
              <div className="w-16 h-16 bg-teal-light rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-mail-send-line text-3xl text-teal"></i>
              </div>
              <h2 className="text-2xl font-bold text-navy mb-4">
                Get the latest missed call research
              </h2>
              <p className="text-gray-600 mb-8">
                Join 2,500+ service business owners getting weekly insights on lead capture and AI phone technology.
              </p>
              <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <input 
                  type="email" 
                  name="email"
                  placeholder="Enter your email" 
                  className="flex-1 px-5 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal focus:border-transparent outline-none text-base"
                  required
                />
                <button 
                  type="submit" 
                  className="px-6 py-3 bg-teal text-white font-semibold rounded-lg hover:bg-teal/90 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Subscribe
                </button>
              </form>
              <p className="text-xs text-gray-500 mt-4">No spam. Unsubscribe anytime.</p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-navy via-navy/95 to-navy/90 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to stop missing calls?
            </h2>
            <p className="text-xl text-white/90 mb-10">
              See how PointCalls can transform your business with a personalized demo.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a 
                href="https://tidycal.com/mattv/15-minute-meeting"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-teal text-white text-lg font-semibold rounded-lg hover:bg-teal/90 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Book a Demo
              </a>
              <a 
                href="tel:+16787376473" 
                className="px-10 py-4 border-2 border-white/30 text-white font-semibold rounded-lg hover:bg-white/10 transition-all duration-200 text-lg whitespace-nowrap cursor-pointer"
              >
                Call Live Demo
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPage;
